import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../services/api'

const PERMIT_TYPES = {
  hot_work: { label: 'Hot Work', icon: '🔥', desc: 'Welding, cutting, grinding' },
  confined_space: { label: 'Confined Space Entry', icon: '🕳️', desc: 'Tanks, pipes, pits (WHS Reg 66-72)' },
  electrical_isolation: { label: 'Electrical Isolation / LOTO', icon: '⚡', desc: 'WHS Reg 154-163' },
  heights: { label: 'Working at Heights', icon: '🏗️', desc: 'WHS Reg 79-82' },
  excavation: { label: 'Excavation / Trenching', icon: '⛏️', desc: 'Dial Before You Dig required' },
  general: { label: 'General Work', icon: '📋', desc: 'Other controlled work' },
}

export default function PermitNew() {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [form, setForm] = useState({
    permit_type: '', location_description: '', work_description: '',
    hazards_identified: [] as string[], controls_required: [] as string[],
    valid_from: '', valid_to: '', workers: [] as string[],
    atmospheric_test_required: false, rescue_plan_in_place: false,
  })
  const [newHazard, setNewHazard] = useState('')
  const [newControl, setNewControl] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async () => {
    setLoading(true)
    setError('')
    try {
      const { data } = await api.post('/permits', form)
      navigate(`/permits/${data.id}`)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create permit')
    }
    setLoading(false)
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>New Permit to Work</h1>

      {step === 1 && (
        <div className="space-y-3">
          <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Select Permit Type</h2>
          {Object.entries(PERMIT_TYPES).map(([key, pt]) => (
            <button key={key} onClick={() => { setForm(f => ({ ...f, permit_type: key })); setStep(2) }}
              className="card w-full flex items-center gap-4 text-left hover:shadow-md transition-all">
              <span className="text-3xl">{pt.icon}</span>
              <div>
                <p className="font-semibold" style={{ color: 'var(--text)' }}>{pt.label}</p>
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{pt.desc}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <div className="card space-y-4">
            <h2 className="font-semibold flex items-center gap-2" style={{ color: 'var(--text)' }}>
              <span>{(PERMIT_TYPES as any)[form.permit_type]?.icon}</span>
              {(PERMIT_TYPES as any)[form.permit_type]?.label}
            </h2>

            <div>
              <label>Location *</label>
              <input value={form.location_description} onChange={e => setForm(f => ({ ...f, location_description: e.target.value }))} placeholder="Where will the work be performed?" required />
            </div>
            <div>
              <label>Work Description *</label>
              <textarea rows={3} value={form.work_description} onChange={e => setForm(f => ({ ...f, work_description: e.target.value }))} placeholder="Describe the work to be performed..." />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label>Valid From *</label>
                <input type="datetime-local" value={form.valid_from} onChange={e => setForm(f => ({ ...f, valid_from: e.target.value }))} />
              </div>
              <div>
                <label>Valid To *</label>
                <input type="datetime-local" value={form.valid_to} onChange={e => setForm(f => ({ ...f, valid_to: e.target.value }))} />
              </div>
            </div>

            {/* Hazards */}
            <div>
              <label>Hazards Identified</label>
              <div className="flex gap-2 mb-2">
                <input value={newHazard} onChange={e => setNewHazard(e.target.value)} placeholder="Add hazard..." onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (newHazard) { setForm(f => ({ ...f, hazards_identified: [...f.hazards_identified, newHazard] })); setNewHazard('') }}} } />
                <button type="button" className="btn btn-secondary flex-shrink-0" onClick={() => { if (newHazard) { setForm(f => ({ ...f, hazards_identified: [...f.hazards_identified, newHazard] })); setNewHazard('') }}}>Add</button>
              </div>
              <div className="flex flex-wrap gap-2">
                {form.hazards_identified.map((h, i) => (
                  <span key={i} className="badge badge-high cursor-pointer" onClick={() => setForm(f => ({ ...f, hazards_identified: f.hazards_identified.filter((_, j) => j !== i) }))}>{h} ×</span>
                ))}
              </div>
            </div>

            {/* Controls */}
            <div>
              <label>Controls Required</label>
              <div className="flex gap-2 mb-2">
                <input value={newControl} onChange={e => setNewControl(e.target.value)} placeholder="Add control..." onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (newControl) { setForm(f => ({ ...f, controls_required: [...f.controls_required, newControl] })); setNewControl('') }}}} />
                <button type="button" className="btn btn-secondary flex-shrink-0" onClick={() => { if (newControl) { setForm(f => ({ ...f, controls_required: [...f.controls_required, newControl] })); setNewControl('') }}}>Add</button>
              </div>
              <div className="flex flex-wrap gap-2">
                {form.controls_required.map((c, i) => (
                  <span key={i} className="badge badge-low cursor-pointer" onClick={() => setForm(f => ({ ...f, controls_required: f.controls_required.filter((_, j) => j !== i) }))}>{c} ×</span>
                ))}
              </div>
            </div>

            {['confined_space'].includes(form.permit_type) && (
              <label className="flex items-center gap-2 cursor-pointer m-0 font-normal">
                <input type="checkbox" checked={form.atmospheric_test_required} onChange={e => setForm(f => ({ ...f, atmospheric_test_required: e.target.checked }))} className="w-4 h-4" style={{ width: '1rem' }} />
                <span className="text-sm" style={{ color: 'var(--text)' }}>Atmospheric test required</span>
              </label>
            )}

            {['heights', 'confined_space'].includes(form.permit_type) && (
              <label className="flex items-center gap-2 cursor-pointer m-0 font-normal">
                <input type="checkbox" checked={form.rescue_plan_in_place} onChange={e => setForm(f => ({ ...f, rescue_plan_in_place: e.target.checked }))} className="w-4 h-4" style={{ width: '1rem' }} />
                <span className="text-sm" style={{ color: 'var(--text)' }}>Rescue plan in place</span>
              </label>
            )}
          </div>

          {error && <div className="text-sm p-3 rounded-lg" style={{ background: 'var(--red-light)', color: 'var(--red)' }}>{error}</div>}

          <div className="flex gap-3">
            <button onClick={() => setStep(1)} className="btn btn-secondary">← Back</button>
            <button onClick={handleSubmit} disabled={loading || !form.location_description || !form.work_description} className="btn btn-primary">
              {loading ? 'Creating...' : 'Create Permit'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
